import React, { useMemo, useRef, useState, useEffect } from 'react';
import { densifyWaypoints, anchoredDenseWaypoints, centerAt } from '../utils/social';

// A Resolve-style keyframe/curve lane for the SELECTED CLIP. The X axis is the
// clip's own time window (so one clip fills the whole width instead of being
// squished into a sliver of the full-movie timeline); the Y axis is the
// subject's normalized position along the chosen axis (horizontal = cx,
// vertical = cy), with optional Y zoom for fine adjustments.
//
//  • dots       = raw YOLO detections (read-only "checkpoints")
//  • thin line  = the editable keyframe path (linear interp)
//  • bold line  = the export smoothing (EMA at the clip's smoothing amount)
//  • diamonds   = editable keyframes:
//      – drag vertically   → set the crop center at that moment
//      – drag horizontally → retime the keyframe (within the clip)
//      – click             → seek + select · shift-click → add to selection
//  • drag on empty space   → marquee-select keyframes (move/delete several)
//  • Delete / Backspace    → remove the selected keyframes
//  • double-click empty    → add a keyframe at that time + position
//  • Y-zoom (± buttons)    → magnify the value range for finer control
export default function SubjectPathLane({
  dur,
  t,
  track,
  waypoints,
  axis = 'cx',
  color = '#2dd4bf',
  smoothing = 0.35,
  onSeek,
  onCommitWaypoints,
  // Clip window in MASTER time. The lane scales to this range (defaults to the
  // whole movie) so the selected clip's keyframes spread across the full width.
  t0 = 0,
  t1 = 0,
}) {
  const ref = useRef(null);
  const dragRef = useRef(null); // active keyframe drag snapshot
  const marqRef = useRef(null); // active marquee drag state
  const movedRef = useRef(false); // suppress the click that ends a drag/marquee
  const [yZoom, setYZoom] = useState(1); // 1 = full frame range; higher = finer
  const [sel, setSel] = useState([]); // selected keyframe times
  const [marquee, setMarquee] = useState(null); // {x0,y0,x1,y1} in 0..1 fracs

  const editable = !!onCommitWaypoints;
  const samples = track?.samples || [];
  const wps = waypoints || [];

  const winStart = t0 || 0;
  const winEnd = t1 > winStart ? t1 : (dur > 0 ? dur : 1);
  const winDur = winEnd - winStart || 1;
  const inWin = (time) => time >= winStart - 0.05 && time <= winEnd + 0.05;

  const winSamples = useMemo(() => samples.filter((s) => inWin(s.t)), [samples, winStart, winEnd]);
  const winWps = useMemo(() => wps.filter((w) => inWin(w.t)), [wps, winStart, winEnd]);

  const selSet = useMemo(() => new Set(sel), [sel]);

  // Vertical view window: center the zoomed band on the data's mean so the path
  // stays in view, clamped to the valid [0,1] frame range.
  const { yMin, span } = useMemo(() => {
    const vals = [...winSamples.map((s) => s[axis]), ...winWps.map((w) => w[axis])];
    const mean = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0.5;
    const sp = Math.min(1, Math.max(0.08, 1 / yZoom));
    let lo = mean - sp / 2;
    lo = Math.max(0, Math.min(1 - sp, lo));
    return { yMin: lo, span: sp };
  }, [winSamples, winWps, axis, yZoom]);

  const normX = (time) => (time - winStart) / winDur; // 0..1 within the clip
  const normY = (val) => Math.max(0, Math.min(1, (val - yMin) / span)); // 0..1 in view
  const xOf = (time) => normX(time) * 1000;
  const yOf = (val) => normY(val) * 1000;
  const pct = (v) => `${Math.max(0, Math.min(100, v))}%`;
  const clamp01 = (v) => Math.max(0, Math.min(1, v));
  const clampWin = (tt) => Math.max(winStart, Math.min(winEnd, tt));
  const valFromFracY = (fy) => clamp01(yMin + fy * span);

  const rawPts = winSamples.map((s) => `${xOf(s.t)},${yOf(s[axis])}`).join(' ');
  const wpLine = winWps.map((w) => `${xOf(w.t)},${yOf(w[axis])}`).join(' ');
  // Bold line = the export EMA at the clip's smoothing amount, so dragging the
  // smoothing slider previews how the crop actually glides between keyframes.
  // When a track is locked the bake blends its dense samples with the keyframes
  // (anchoredDenseWaypoints) — the lane previews that same blend.
  const basePath = samples.length >= 2 && wps.length ? anchoredDenseWaypoints(samples, wps) : wps;
  const smooth = densifyWaypoints(basePath, winEnd, 15, smoothing).filter((p) => inWin(p.t));
  const smoothPts = smooth.map((p) => `${xOf(p.t)},${yOf(p[axis])}`).join(' ');

  const fracAt = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    };
  };

  // Delete the selected keyframes with the keyboard (Resolve-style). Guarded so
  // it doesn't fire while typing in a form elsewhere on the page.
  useEffect(() => {
    if (!editable || sel.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      onCommitWaypoints(wps.filter((w) => !selSet.has(w.t)));
      setSel([]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editable, sel, selSet, wps, onCommitWaypoints]);

  // Add a keyframe at a clicked time/value; the un-dragged axis inherits the
  // current interpolated center so it doesn't snap back to frame-center.
  const addFromEvent = (e) => {
    if (!editable) return;
    if (e.target.closest && e.target.closest('.cs-pl-wpmark')) return;
    const f = fracAt(e);
    if (!f) return;
    const time = clampWin(winStart + f.x * winDur);
    const c = centerAt(wps, time);
    const wp = { cx: c.cx, cy: c.cy, t: time, [axis]: valFromFracY(f.y) };
    const next = wps.filter((w) => Math.abs(w.t - time) > 0.04).concat(wp);
    onCommitWaypoints(next);
  };

  // Drag a keyframe (or the whole selection if it's part of one): vertical →
  // crop center along the axis, horizontal → retime. Deltas are measured from a
  // start snapshot so a long drag doesn't accumulate rounding drift.
  const startDrag = (origT) => (e) => {
    if (!editable) return;
    e.stopPropagation();
    e.preventDefault();
    movedRef.current = false;
    const f0 = fracAt(e);
    if (!f0) return;
    const grabbedSelected = selSet.has(origT);
    const baseSel = grabbedSelected ? sel.slice() : [origT];
    if (!grabbedSelected) setSel(baseSel);
    const baseWps = wps.map((w) => ({ ...w }));
    const baseY0 = valFromFracY(f0.y);
    dragRef.current = { f0, baseWps, baseSel };
    const move = (ev) => {
      const f = fracAt(ev);
      if (!f) return;
      movedRef.current = true;
      const dt = (f.x - f0.x) * winDur;
      const dval = valFromFracY(f.y) - baseY0;
      const bset = new Set(baseSel);
      const next = baseWps.map((w) =>
        bset.has(w.t) ? { ...w, t: clampWin(w.t + dt), [axis]: clamp01(w[axis] + dval) } : w
      );
      setSel(baseSel.map((tt) => clampWin(tt + dt)));
      onCommitWaypoints(next);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setTimeout(() => { dragRef.current = null; }, 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Pointer-down on empty lane space: start a marquee. A negligible move is
  // treated as a plain click (seek + clear selection) on pointer-up.
  const startMarquee = (e) => {
    if (e.target.closest && e.target.closest('.cs-pl-wpmark')) return;
    if (e.target.closest && e.target.closest('.cs-pl-zoom')) return;
    if (e.target.closest && e.target.closest('.cs-pl-seltools')) return;
    const f0 = fracAt(e);
    if (!f0) return;
    movedRef.current = false;
    marqRef.current = { f0, rect: null };
    const move = (ev) => {
      const f = fracAt(ev);
      if (!f) return;
      if (Math.abs(f.x - f0.x) > 0.006 || Math.abs(f.y - f0.y) > 0.006) movedRef.current = true;
      const rect = { x0: f0.x, y0: f0.y, x1: f.x, y1: f.y };
      marqRef.current.rect = rect;
      setMarquee(rect);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const r = marqRef.current?.rect;
      if (movedRef.current && r) {
        const lo = { x: Math.min(r.x0, r.x1), y: Math.min(r.y0, r.y1) };
        const hi = { x: Math.max(r.x0, r.x1), y: Math.max(r.y0, r.y1) };
        const hits = winWps
          .filter((w) => {
            const x = normX(w.t);
            const y = normY(w[axis]);
            return x >= lo.x && x <= hi.x && y >= lo.y && y <= hi.y;
          })
          .map((w) => w.t);
        setSel(hits);
      } else if (onSeek) {
        setSel([]);
        onSeek(clampWin(winStart + f0.x * winDur));
      }
      marqRef.current = null;
      setMarquee(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const edge = axis === 'cx' ? ['L', 'C', 'R'] : ['T', 'C', 'B'];
  const center05 = yMin <= 0.5 && 0.5 <= yMin + span; // frame center visible?
  const zoomBy = (mult) => setYZoom((z) => Math.max(1, Math.min(8, Math.round(z * mult * 10) / 10)));
  const deleteSel = () => {
    if (!editable || sel.length === 0) return;
    onCommitWaypoints(wps.filter((w) => !selSet.has(w.t)));
    setSel([]);
  };
  const deleteOne = (wt) => {
    if (!editable) return;
    onCommitWaypoints(wps.filter((w) => w.t !== wt));
    setSel((cur) => cur.filter((x) => x !== wt));
  };

  return (
    <div
      className="cs-pathlane"
      ref={ref}
      onPointerDown={editable ? startMarquee : undefined}
      onClick={!editable && onSeek ? (e) => { const f = fracAt(e); if (f) onSeek(winStart + f.x * winDur); } : undefined}
      onDoubleClick={addFromEvent}
      title={editable ? 'Drag to box-select · double-click to add a keyframe · drag a diamond to adjust' : undefined}
    >
      <div className="cs-pathlane-axis">
        <span>{edge[0]}</span>
        <span>{edge[1]}</span>
        <span>{edge[2]}</span>
      </div>

      <svg className="cs-pathlane-svg" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden>
        {center05 && <line x1="0" y1={yOf(0.5)} x2="1000" y2={yOf(0.5)} className="cs-pl-mid" />}
        {rawPts && <polyline points={rawPts} className="cs-pl-raw" />}
        {wpLine && <polyline points={wpLine} className="cs-pl-wp" style={{ stroke: color }} />}
        {smoothPts && <polyline points={smoothPts} className="cs-pl-smooth" style={{ stroke: color }} />}
      </svg>

      {/* Raw detection checkpoints */}
      {winSamples.map((s, i) => (
        <span
          key={`s${i}`}
          className="cs-pl-dot"
          style={{ left: pct(normX(s.t) * 100), top: pct(normY(s[axis]) * 100) }}
          title={`detection @ ${s.t.toFixed(2)}s`}
        />
      ))}

      {/* Editable keyframes — drag to adjust, click to select/seek */}
      {winWps.map((w) => (
        <button
          key={w.t}
          className={`cs-pl-wpmark${selSet.has(w.t) ? ' is-sel' : ''}`}
          style={{ left: pct(normX(w.t) * 100), top: pct(normY(w[axis]) * 100), borderColor: color }}
          title={`keyframe @ ${w.t.toFixed(2)}s — drag to adjust · click to select/seek · shift-click to multi-select · alt-click or right-click to delete`}
          onPointerDown={editable ? startDrag(w.t) : undefined}
          onContextMenu={editable ? (e) => { e.preventDefault(); e.stopPropagation(); deleteOne(w.t); } : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (dragRef.current && movedRef.current) return; // ignore the click ending a drag
            if (e.altKey) {
              deleteOne(w.t);
            } else if (e.shiftKey) {
              setSel((cur) => (cur.includes(w.t) ? cur.filter((x) => x !== w.t) : [...cur, w.t]));
            } else {
              setSel([w.t]);
              onSeek?.(w.t);
            }
          }}
        />
      ))}

      {/* Marquee box while dragging on empty space */}
      {marquee && (
        <div
          className="cs-pl-marquee"
          style={{
            left: pct(Math.min(marquee.x0, marquee.x1) * 100),
            top: pct(Math.min(marquee.y0, marquee.y1) * 100),
            width: pct(Math.abs(marquee.x1 - marquee.x0) * 100),
            height: pct(Math.abs(marquee.y1 - marquee.y0) * 100),
          }}
        />
      )}

      <div className="cs-pl-playhead" style={{ left: pct(normX(t) * 100) }} />

      {/* First-run hint: no keyframes yet → show how to create them. */}
      {winWps.length === 0 && (
        <div className="cs-pl-hint">
          Double-click to add a tracking keyframe — or pick a subject (★) in the panel to auto-track
        </div>
      )}

      {/* Selection tools (appear once keyframes are selected). */}
      {sel.length > 0 && (
        <div className="cs-pl-seltools" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
          <span>{sel.length} selected</span>
          <button type="button" title="Delete selected keyframes (Delete)" onClick={deleteSel}>Delete</button>
          <button type="button" title="Clear selection" onClick={() => setSel([])}>✕</button>
        </div>
      )}

      {/* Y zoom (finer center control). */}
      <div className="cs-pl-zoom" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
        <button type="button" title="Zoom out the value axis" aria-label="Zoom out value axis" onClick={() => zoomBy(1 / 1.5)} disabled={yZoom <= 1}>−</button>
        <span title="Vertical (value) zoom">{yZoom.toFixed(1)}×</span>
        <button type="button" title="Zoom in for finer center control" aria-label="Zoom in value axis" onClick={() => zoomBy(1.5)} disabled={yZoom >= 8}>＋</button>
      </div>

      {/* Clip-window time ticks for orientation (Resolve-style). */}
      <div className="cs-pl-ticks">
        <span>{winStart.toFixed(1)}s</span>
        <span>{((winStart + winEnd) / 2).toFixed(1)}s</span>
        <span>{winEnd.toFixed(1)}s</span>
      </div>
    </div>
  );
}
